import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Package, ChevronRight, Home } from 'lucide-react'
import {
  getInfo, getCatalogo, getCategorias, fmtPreco,
  type LojaProduto, type LojaCategoria, type LojaSubgrupo,
} from './lojaApi'
import LojaLayout from './LojaLayout'
import { enterAbrePrimeiro } from '@/lib/keyboard'

export default function LojaHome() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const [busca, setBusca] = useState('')
  const [grupoId, setGrupoId] = useState<number | null>(null)
  const [subgrupoId, setSubgrupoId] = useState<number | null>(null)

  const { data: info } = useQuery({
    queryKey: ['loja-info', slug],
    queryFn: () => getInfo(slug),
    enabled: !!slug,
  })

  const { data: categorias = [] } = useQuery<LojaCategoria[]>({
    queryKey: ['loja-categorias', slug],
    queryFn: () => getCategorias(slug),
    enabled: !!slug,
  })

  const grupoSelecionado = categorias.find(g => g.id === grupoId) || null
  const subgrupoSelecionado: LojaSubgrupo | null = grupoSelecionado
    ? (grupoSelecionado.subgrupos?.find(s => s.id === subgrupoId) || null)
    : null

  const { data: catalogo, isLoading } = useQuery({
    queryKey: ['loja-catalogo', slug, busca, grupoId, subgrupoId],
    queryFn: () => getCatalogo(slug, {
      q: busca || undefined,
      grupo_id: grupoId || undefined,
      subgrupo_id: subgrupoId || undefined,
      page: 1,
      page_size: 40,
    }),
    enabled: !!slug,
  })

  const produtos = catalogo?.itens || []

  const selecionarGrupo = (id: number | null) => {
    setGrupoId(id)
    setSubgrupoId(null)
  }

  return (
    <LojaLayout info={info || null} slug={slug}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-5">
        {/* HEADER DA PAGINA */}
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-ink-900">
            {info?.nome || 'Carregando...'}
          </h1>
          {info?.descricao && (
            <p className="text-sm text-ink-600 mt-1 max-w-2xl">{info.descricao}</p>
          )}
        </div>

        {/* Breadcrumb */}
        {(grupoSelecionado || subgrupoSelecionado) && (
          <nav className="flex items-center gap-1.5 text-sm text-ink-600">
            <button onClick={() => selecionarGrupo(null)}
              className="flex items-center gap-1 hover:text-brand-600 transition">
              <Home size={13} /> Início
            </button>
            {grupoSelecionado && (
              <>
                <ChevronRight size={13} className="text-ink-300" />
                <button onClick={() => setSubgrupoId(null)}
                  className={`hover:text-brand-600 transition ${!subgrupoSelecionado ? 'text-ink-900 font-medium' : ''}`}>
                  {grupoSelecionado.nome}
                </button>
              </>
            )}
            {subgrupoSelecionado && (
              <>
                <ChevronRight size={13} className="text-ink-300" />
                <span className="text-ink-900 font-medium">{subgrupoSelecionado.nome}</span>
              </>
            )}
          </nav>
        )}

        {/* BUSCA + GRUPOS + SUBGRUPOS */}
        <div className="card card-body space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              type="text"
              className="input pl-9"
              placeholder="Buscar produto por nome, código ou EAN..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              onKeyDown={enterAbrePrimeiro(produtos, p => navigate(`/loja/${slug}/produto/${p.sku}`))}
            />
          </div>

          {categorias.length > 0 && (
            <>
              <div className="flex flex-wrap gap-2">
                <Chip active={grupoId === null} onClick={() => selecionarGrupo(null)}>
                  Todos
                </Chip>
                {categorias.map(g => (
                  <Chip
                    key={g.id}
                    active={grupoId === g.id}
                    onClick={() => selecionarGrupo(g.id)}
                  >
                    {g.nome}
                    {g.qtd_produtos != null && (
                      <span className={`ml-1.5 text-[10px] ${grupoId === g.id ? 'text-brand-100' : 'text-ink-500'}`}>
                        {g.qtd_produtos}
                      </span>
                    )}
                  </Chip>
                ))}
              </div>

              {grupoSelecionado?.subgrupos && grupoSelecionado.subgrupos.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-ink-100">
                  <SubChip active={subgrupoId === null} onClick={() => setSubgrupoId(null)}>
                    Todos os subgrupos
                  </SubChip>
                  {grupoSelecionado.subgrupos.map(s => (
                    <SubChip
                      key={s.id}
                      active={subgrupoId === s.id}
                      onClick={() => setSubgrupoId(s.id)}
                    >
                      {s.nome}
                      <span className={`ml-1.5 text-[10px] ${subgrupoId === s.id ? 'text-brand-100' : 'text-ink-400'}`}>
                        {s.qtd_produtos}
                      </span>
                    </SubChip>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* RESULTADO: contador + grid */}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-sm text-ink-600">
              {isLoading
                ? 'Carregando catálogo...'
                : `${produtos.length} ${produtos.length === 1 ? 'produto' : 'produtos'}`}
              {!isLoading && catalogo && catalogo.total > produtos.length && (
                <span className="text-ink-400"> · de {catalogo.total}</span>
              )}
            </div>
          </div>

          {isLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="card">
                  <div className="aspect-square bg-ink-100 rounded-t-md animate-pulse" />
                  <div className="card-body space-y-2">
                    <div className="h-4 bg-ink-100 rounded animate-pulse" />
                    <div className="h-3 bg-ink-100 rounded w-1/2 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && produtos.length === 0 && (
            <div className="card card-body text-center py-12 text-ink-500">
              <Package size={32} className="mx-auto mb-2 text-ink-300" />
              <div className="font-medium text-ink-700">
                {busca ? `Nenhum resultado para "${busca}"` : 'Nenhum produto disponível no momento'}
              </div>
              <div className="text-xs mt-1">
                {busca ? 'Tente outra busca ou remova os filtros.' : 'Volte em breve.'}
              </div>
            </div>
          )}

          {!isLoading && produtos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {produtos.map(p => <Card key={p.id} produto={p} slug={slug} />)}
            </div>
          )}
        </div>
      </div>
    </LojaLayout>
  )
}

function Chip({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
        active
          ? 'bg-brand-600 text-white'
          : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
      }`}
    >
      {children}
    </button>
  )
}

function SubChip({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
        active
          ? 'bg-brand-500 text-white'
          : 'bg-white border border-ink-200 text-ink-600 hover:border-brand-400 hover:text-brand-700'
      }`}
    >
      {children}
    </button>
  )
}

function Card({ produto, slug }: { produto: LojaProduto; slug: string }) {
  return (
    <Link
      to={`/loja/${slug}/produto/${produto.sku}`}
      className="card hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 block group"
    >
      <div className="aspect-square bg-ink-50 rounded-t-md overflow-hidden relative">
        {produto.foto ? (
          <img
            src={produto.foto}
            alt={produto.descricao}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink-300">
            <Package size={36} />
          </div>
        )}
      </div>
      <div className="card-body">
        <div className="text-sm font-medium text-ink-900 line-clamp-2 mb-1 min-h-[2.5rem]">
          {produto.descricao}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-400 mb-2">
          {produto.sku}
        </div>
        <div className="flex items-baseline gap-1 pt-2 border-t border-ink-100">
          <span className="text-xs text-ink-500">R$</span>
          <span className="text-lg font-bold text-ink-900">{fmtPreco(produto.preco)}</span>
          <span className="text-[10px] text-ink-400 ml-auto uppercase">/ {produto.unidade}</span>
        </div>
      </div>
    </Link>
  )
}
